import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import type { User, Flight, Friendship, Message, GroupChat, GroupMember, GroupMessage, ConversationDeletion, MeetupProposalMetadata, GroupMessageMetadata, Meetup, MeetupRsvp } from '../types';
import { hashPhone } from '../utils/phoneHash';
import { toDateStr } from '../utils/dates';

// ── Row type aliases ──

type ProfileRow = Database['public']['Tables']['profiles']['Row'];
type ProfileUpdate = Database['public']['Tables']['profiles']['Update'];
type FlightRow = Database['public']['Tables']['flights']['Row'];
type FlightInsert = Database['public']['Tables']['flights']['Insert'];
type FriendshipRow = Database['public']['Tables']['friendships']['Row'];
type FriendshipInsert = Database['public']['Tables']['friendships']['Insert'];
type ImportedContactRow = Database['public']['Tables']['imported_contacts']['Row'];
type ImportedContactInsert = Database['public']['Tables']['imported_contacts']['Insert'];

// ── Typed query helpers ──
// The Database type is hand-written and lacks the Relationships/Views/Functions
// fields that @supabase/supabase-js v2.97+ expects, causing queries to resolve
// as `never`. These helpers provide typed wrappers around the raw PostgREST
// responses so every call site stays type-safe without modifying database.types.ts.

async function selectProfileWhere(column: string, value: string) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq(column as 'id', value)
    .single();
  return { data: data as ProfileRow | null, error };
}

// Non-self queries use profiles_public view (excludes raw phone)
async function selectPublicProfileIlike(column: string, value: string) {
  const { data, error } = await supabase
    .from('profiles_public' as 'profiles')
    .select('*')
    .ilike(column as 'handle', value)
    .single();
  return { data: data as ProfileRow | null, error };
}

async function selectPublicProfilesIn(column: string, values: string[]) {
  const { data, error } = await supabase
    .from('profiles_public' as 'profiles')
    .select('*')
    .in(column as 'id', values);
  return { data: data as ProfileRow[] | null, error };
}

async function selectPublicProfilesInNeq(
  inColumn: string,
  inValues: string[],
  neqColumn: string,
  neqValue: string
) {
  const { data, error } = await supabase
    .from('profiles_public' as 'profiles')
    .select('*')
    .in(inColumn as 'phone_hash', inValues)
    .neq(neqColumn as 'id', neqValue);
  return { data: data as ProfileRow[] | null, error };
}

// ── Mappers: DB rows (snake_case) <-> App types (camelCase) ──

function mapRowToUser(row: ProfileRow): User {
  return {
    id: row.id,
    handle: row.handle,
    name: row.name,
    airlineCode: row.airline_code,
    baseAirport: row.base_airport,
    tier: (row.tier === 'premium' ? 'premium' : 'free') as User['tier'],
    ...(row.avatar_url != null ? { avatarUrl: row.avatar_url } : {}),
    hasPhone: !!row.phone_hash,
    ...(row.instagram_handle != null ? { instagramHandle: row.instagram_handle } : {}),
    ...(row.tier_expires_at != null ? { tierExpiresAt: row.tier_expires_at } : {}),
    ...(row.referred_by != null ? { referredBy: row.referred_by } : {}),
    ...(row.leaderboard_visible != null ? { leaderboardVisible: row.leaderboard_visible } : {}),
  };
}

function mapRowToFlight(row: FlightRow): Flight {
  return {
    id: row.id,
    userId: row.user_id,
    flightNumber: row.flight_number,
    date: row.date,
    departureAirport: row.departure_airport,
    arrivalAirport: row.arrival_airport,
    airlineCode: row.airline_code,
    ...(row.departure_time != null
      ? { departureTime: row.departure_time }
      : {}),
    ...(row.arrival_time != null ? { arrivalTime: row.arrival_time } : {}),
    ...(row.aircraft_type != null ? { aircraftType: row.aircraft_type } : {}),
    ...(row.source != null
      ? { source: row.source as Flight['source'] }
      : {}),
  };
}

function mapFlightToRow(flight: Omit<Flight, 'id'>): FlightInsert {
  return {
    user_id: flight.userId,
    flight_number: flight.flightNumber,
    date: flight.date,
    departure_airport: flight.departureAirport,
    arrival_airport: flight.arrivalAirport,
    airline_code: flight.airlineCode,
    departure_time: flight.departureTime ?? null,
    arrival_time: flight.arrivalTime ?? null,
    aircraft_type: flight.aircraftType ?? null,
    source: flight.source ?? null,
  };
}

function mapRowToFriendship(row: FriendshipRow): Friendship {
  return {
    id: row.id,
    fromUserId: row.from_user_id,
    toUserId: row.to_user_id,
    status: row.status,
    createdAt: row.created_at,
  };
}

function mapUserToProfileUpdate(updates: Partial<User>): ProfileUpdate {
  const mapped: ProfileUpdate = {};
  if (updates.handle !== undefined) mapped.handle = updates.handle;
  if (updates.name !== undefined) mapped.name = updates.name;
  if (updates.airlineCode !== undefined)
    mapped.airline_code = updates.airlineCode;
  if (updates.baseAirport !== undefined)
    mapped.base_airport = updates.baseAirport;
  if (updates.avatarUrl !== undefined)
    mapped.avatar_url = updates.avatarUrl ?? null;
  // Raw phone is never stored server-side — only the hash
  if (updates.phone !== undefined) mapped.phone = null;
  if (updates.instagramHandle !== undefined)
    mapped.instagram_handle = updates.instagramHandle ?? null;
  if (updates.leaderboardVisible !== undefined)
    mapped.leaderboard_visible = updates.leaderboardVisible;
  return mapped;
}

// ── Profile functions ──

export async function getProfile(id: string): Promise<User | null> {
  const { data, error } = await selectProfileWhere('id', id);
  if (error || !data) return null;
  return mapRowToUser(data);
}

export async function getProfilesBatch(ids: string[]): Promise<User[]> {
  if (ids.length === 0) return [];
  const { data, error } = await selectPublicProfilesIn('id', ids);
  if (error || !data) return [];
  return data.map(mapRowToUser);
}

export async function getReferralCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('profiles_public' as 'profiles')
    .select('*', { count: 'exact', head: true })
    .eq('referred_by' as 'id', userId);
  if (error) return 0;
  return count ?? 0;
}

export async function resolveReferralHandle(handle: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('profiles_public' as 'profiles')
    .select('id')
    .ilike('handle' as 'id', handle)
    .single();
  if (error || !data) return null;
  return (data as { id: string }).id;
}

export async function getProfileByHandle(
  handle: string
): Promise<User | null> {
  const { data, error } = await selectPublicProfileIlike('handle', handle);
  if (error || !data) return null;
  return mapRowToUser(data);
}

export async function updateProfile(
  userId: string,
  updates: Partial<User>
): Promise<void> {
  const mapped = mapUserToProfileUpdate(updates);
  // Recompute phone_hash when phone changes
  if (updates.phone !== undefined) {
    mapped.phone_hash = updates.phone ? await hashPhone(updates.phone) : null;
  }
  const { error } = await supabase
    .from('profiles')
    .update(mapped as never)
    .eq('id' as never, userId);

  if (error) throw error;
}

// ── Flight functions ──

export async function getFlightsForUser(userId: string): Promise<Flight[]> {
  const { data, error } = await supabase
    .from('flights')
    .select('*')
    .eq('user_id' as never, userId)
    .order('date' as never, { ascending: true });

  if (error) throw error;
  return ((data ?? []) as FlightRow[]).map(mapRowToFlight);
}

export async function getAllAccessibleFlights(
  userId: string,
  friendIds: string[]
): Promise<Flight[]> {
  const allUserIds = [userId, ...friendIds];

  // Only fetch flights from 6 months ago onward to avoid unbounded growth
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  const cutoff = toDateStr(sixMonthsAgo);

  const { data, error } = await supabase
    .from('flights')
    .select('*')
    .in('user_id' as never, allUserIds)
    .gte('date' as never, cutoff)
    .order('date' as never, { ascending: true });

  if (error) throw error;
  return ((data ?? []) as FlightRow[]).map(mapRowToFlight);
}

/** Fetch ALL past flights for a set of user IDs (no date cutoff). Used for leaderboard totals. */
export async function getAllFlightsForUsers(userIds: string[]): Promise<Flight[]> {
  if (userIds.length === 0) return [];
  const today = toDateStr(new Date());
  const { data, error } = await supabase
    .from('flights')
    .select('*')
    .in('user_id' as never, userIds)
    .lte('date' as never, today)
    .order('date' as never, { ascending: true });

  if (error) throw error;
  return ((data ?? []) as FlightRow[]).map(mapRowToFlight);
}

// Look up aircraft_type from flight_schedules for flights missing it
async function enrichAircraftType<T extends Omit<Flight, 'id'>>(flights: T[]): Promise<T[]> {
  const needLookup = flights.filter((f) => !f.aircraftType && f.flightNumber);
  if (needLookup.length === 0) return flights;

  const flightNumbers = [...new Set(needLookup.map((f) => f.flightNumber))];
  const { data } = await supabase
    .from('flight_schedules')
    .select('flight_number, aircraft_type')
    .in('flight_number' as never, flightNumbers as never)
    .not('aircraft_type' as never, 'is' as never, null)
    .limit(flightNumbers.length * 2);

  if (!data || data.length === 0) return flights;

  // Build map: flight_number → aircraft_type (first match)
  const typeMap = new Map<string, string>();
  for (const row of data as { flight_number: string; aircraft_type: string }[]) {
    if (!typeMap.has(row.flight_number)) {
      typeMap.set(row.flight_number, row.aircraft_type);
    }
  }

  return flights.map((f) => {
    if (f.aircraftType || !f.flightNumber) return f;
    const type = typeMap.get(f.flightNumber);
    return type ? { ...f, aircraftType: type } : f;
  });
}

export async function addFlight(flight: Omit<Flight, 'id'>): Promise<Flight> {
  const [enriched] = await enrichAircraftType([flight]);
  const row = mapFlightToRow(enriched);
  const { data, error } = await supabase
    .from('flights')
    .insert(row as never)
    .select()
    .single();

  if (error || !data) throw error ?? new Error('Failed to insert flight');
  return mapRowToFlight(data as FlightRow);
}

export interface AddFlightsResult {
  inserted: Flight[];
  duplicatesSkipped: number;
}

export async function addFlights(
  flights: Omit<Flight, 'id'>[]
): Promise<AddFlightsResult> {
  if (flights.length === 0) return { inserted: [], duplicatesSkipped: 0 };

  // Determine the date range covered by the incoming flights so we can fetch
  // only the relevant existing flights for the user.
  const userId = flights[0].userId;
  const dates = flights.map((f) => f.date).sort();
  const minDate = dates[0];
  const maxDate = dates[dates.length - 1];

  // Fetch existing flights for this user within the date range
  const { data: existingData, error: fetchError } = await supabase
    .from('flights')
    .select('*')
    .eq('user_id' as never, userId)
    .gte('date' as never, minDate)
    .lte('date' as never, maxDate);

  if (fetchError) throw fetchError;

  const existingFlights = ((existingData ?? []) as FlightRow[]).map(mapRowToFlight);

  // Build a set of keys for existing flights for O(1) lookup
  const existingKeys = new Set(
    existingFlights.map(
      (f) => `${f.flightNumber}|${f.date}|${f.departureAirport}|${f.arrivalAirport}`
    )
  );

  // Filter out duplicates
  const newFlights = flights.filter((f) => {
    const key = `${f.flightNumber}|${f.date}|${f.departureAirport}|${f.arrivalAirport}`;
    return !existingKeys.has(key);
  });

  const duplicatesSkipped = flights.length - newFlights.length;

  if (duplicatesSkipped > 0) {
    console.info(
      `[supabaseService] addFlights: skipped ${duplicatesSkipped} duplicate(s) out of ${flights.length} flight(s)`
    );
  }

  if (newFlights.length === 0) {
    return { inserted: [], duplicatesSkipped };
  }

  const enriched = await enrichAircraftType(newFlights);
  const rows = enriched.map(mapFlightToRow);
  const { data, error } = await supabase
    .from('flights')
    .insert(rows as never)
    .select();

  if (error) throw error;
  return {
    inserted: ((data ?? []) as FlightRow[]).map(mapRowToFlight),
    duplicatesSkipped,
  };
}

export async function updateFlight(
  flightId: string,
  updates: Partial<Omit<Flight, 'id' | 'userId'>>
): Promise<void> {
  const mapped: Record<string, unknown> = {};
  if (updates.flightNumber !== undefined) mapped.flight_number = updates.flightNumber;
  if (updates.date !== undefined) mapped.date = updates.date;
  if (updates.departureAirport !== undefined) mapped.departure_airport = updates.departureAirport;
  if (updates.arrivalAirport !== undefined) mapped.arrival_airport = updates.arrivalAirport;
  if (updates.departureTime !== undefined) mapped.departure_time = updates.departureTime ?? null;
  if (updates.arrivalTime !== undefined) mapped.arrival_time = updates.arrivalTime ?? null;
  if (updates.airlineCode !== undefined) mapped.airline_code = updates.airlineCode;
  if (updates.aircraftType !== undefined) mapped.aircraft_type = updates.aircraftType ?? null;
  if (updates.source !== undefined) mapped.source = updates.source ?? null;

  const { error } = await supabase
    .from('flights')
    .update(mapped as never)
    .eq('id' as never, flightId);

  if (error) throw error;
}

export async function removeFlight(flightId: string): Promise<void> {
  const { error } = await supabase
    .from('flights')
    .delete()
    .eq('id' as never, flightId);

  if (error) throw error;
}

// ── Flight Match functions ──

export interface FlightMatchCount {
  flightNumber: string;
  date: string;
  count: number;
}

export async function getFlightMatchCounts(
  flightNumbers: string[],
  dates: string[],
): Promise<FlightMatchCount[]> {
  if (flightNumbers.length === 0) return [];

  const { data, error } = await supabase.rpc(
    'flight_match_counts' as never,
    {
      p_flight_numbers: flightNumbers,
      p_dates: dates,
    } as never,
  );

  if (error) {
    console.warn('[supabaseService] getFlightMatchCounts failed:', error);
    return [];
  }

  return ((data ?? []) as Array<{ flight_number: string; flight_date: string; match_count: number }>).map(
    (row) => ({
      flightNumber: row.flight_number,
      date: row.flight_date,
      count: Number(row.match_count),
    }),
  );
}

// ── Friendship functions ──

export async function getFriendships(
  userId: string
): Promise<Friendship[]> {
  const { data, error } = await supabase
    .from('friendships')
    .select('*')
    .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`);

  if (error) throw error;
  return ((data ?? []) as FriendshipRow[]).map(mapRowToFriendship);
}

export async function getFriends(userId: string): Promise<User[]> {
  // Fetch accepted friendships involving this user
  const { data: friendships, error: fError } = await supabase
    .from('friendships')
    .select('from_user_id, to_user_id')
    .eq('status' as never, 'accepted')
    .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`);

  if (fError) throw fError;
  if (!friendships || friendships.length === 0) return [];

  // Collect friend IDs — cast rows since the partial select resolves to never
  type FriendPair = { from_user_id: string; to_user_id: string };
  const pairs = friendships as FriendPair[];
  const friendIds = pairs.map((f) =>
    f.from_user_id === userId ? f.to_user_id : f.from_user_id
  );

  // Fetch friend profiles
  const { data: profiles, error: pError } = await selectPublicProfilesIn(
    'id',
    friendIds
  );

  if (pError) throw pError;
  return (profiles ?? []).map(mapRowToUser);
}

export async function getPendingRequests(
  userId: string
): Promise<Friendship[]> {
  const { data, error } = await supabase
    .from('friendships')
    .select('*')
    .eq('to_user_id' as never, userId)
    .eq('status' as never, 'pending');

  if (error) throw error;
  return ((data ?? []) as FriendshipRow[]).map(mapRowToFriendship);
}

export async function sendFriendRequest(
  fromUserId: string,
  toUserId: string
): Promise<Friendship | null> {
  // Check if a friendship already exists in either direction (skip rejected — allow re-send)
  const { data: existing } = await supabase
    .from('friendships')
    .select('id, status')
    .or(
      `and(from_user_id.eq.${fromUserId},to_user_id.eq.${toUserId}),and(from_user_id.eq.${toUserId},to_user_id.eq.${fromUserId})`
    )
    .limit(1);

  if (existing && existing.length > 0) {
    const row = existing[0] as { id: string; status: string };
    if (row.status === 'rejected') {
      // Delete the rejected row so a fresh request can be sent
      await supabase.from('friendships').delete().eq('id' as never, row.id);
    } else {
      return null; // pending or accepted — don't duplicate
    }
  }

  const row: FriendshipInsert = {
    from_user_id: fromUserId,
    to_user_id: toUserId,
    status: 'pending',
  };

  const { data, error } = await supabase
    .from('friendships')
    .insert(row as never)
    .select()
    .single();

  if (error || !data)
    throw error ?? new Error('Failed to send friend request');
  return mapRowToFriendship(data as FriendshipRow);
}

export async function acceptFriendRequest(
  friendshipId: string
): Promise<void> {
  const { error } = await supabase
    .from('friendships')
    .update({ status: 'accepted' } as never)
    .eq('id' as never, friendshipId);

  if (error) throw error;
}

export async function rejectFriendRequest(
  friendshipId: string
): Promise<void> {
  const { error } = await supabase
    .from('friendships')
    .update({ status: 'rejected' } as never)
    .eq('id' as never, friendshipId);

  if (error) throw error;
}

export async function removeFriend(
  userId: string,
  friendUserId: string
): Promise<void> {
  const { error } = await supabase
    .from('friendships')
    .delete()
    .or(
      `and(from_user_id.eq.${userId},to_user_id.eq.${friendUserId}),and(from_user_id.eq.${friendUserId},to_user_id.eq.${userId})`
    );

  if (error) throw error;
}

// ── Contact matching functions ──

export async function storeImportedContacts(
  userId: string,
  contacts: { phone_hash: string }[]
): Promise<void> {
  if (contacts.length === 0) return;

  const rows: ImportedContactInsert[] = contacts.map((c) => ({
    user_id: userId,
    phone_hash: c.phone_hash,
  }));

  const { error } = await supabase
    .from('imported_contacts')
    .upsert(rows as never, { onConflict: 'user_id,phone_hash' });

  if (error) throw error;
}

export async function matchContactsToUsers(
  userId: string
): Promise<User[]> {
  // Fetch all imported contact phone hashes for this user
  const { data: contacts, error: cError } = await supabase
    .from('imported_contacts')
    .select('phone_hash')
    .eq('user_id' as never, userId);

  if (cError) throw cError;
  if (!contacts || contacts.length === 0) return [];

  const hashes = (contacts as Pick<ImportedContactRow, 'phone_hash'>[]).map(
    (c) => c.phone_hash
  );

  // Find profiles with matching phone hashes, excluding self
  const { data: profiles, error: pError } = await selectPublicProfilesInNeq(
    'phone_hash',
    hashes,
    'id',
    userId
  );

  if (pError) throw pError;
  return (profiles ?? []).map(mapRowToUser);
}

export async function hasImportedContacts(
  userId: string
): Promise<boolean> {
  const { count, error } = await supabase
    .from('imported_contacts')
    .select('id', { count: 'exact', head: true })
    .eq('user_id' as never, userId);

  if (error) throw error;
  return (count ?? 0) > 0;
}

export async function deleteImportedContacts(userId: string): Promise<void> {
  const { error } = await supabase
    .from('imported_contacts')
    .delete()
    .eq('user_id' as never, userId);

  if (error) throw error;
}

// ── Message functions ──

interface MessageRow {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  read_at: string | null;
  edited_at: string | null;
  created_at: string;
}

function mapRowToMessage(row: MessageRow): Message {
  return {
    id: row.id,
    senderId: row.sender_id,
    receiverId: row.receiver_id,
    content: row.content,
    readAt: row.read_at,
    editedAt: row.edited_at,
    createdAt: row.created_at,
  };
}

export async function getMessages(userId: string): Promise<Message[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .or(`sender_id.eq.${userId},receiver_id.eq.${userId}`)
    .order('created_at' as never, { ascending: true })
    .limit(500);

  if (error) throw error;
  return ((data ?? []) as MessageRow[]).map(mapRowToMessage);
}

export async function sendMessage(
  senderId: string,
  receiverId: string,
  content: string
): Promise<Message> {
  const { data, error } = await supabase
    .from('messages')
    .insert({ sender_id: senderId, receiver_id: receiverId, content } as never)
    .select()
    .single();

  if (error || !data) throw error ?? new Error('Failed to send message');
  return mapRowToMessage(data as MessageRow);
}

export async function markMessagesRead(
  receiverId: string,
  senderId: string
): Promise<void> {
  const { error } = await supabase
    .from('messages')
    .update({ read_at: new Date().toISOString() } as never)
    .eq('receiver_id' as never, receiverId)
    .eq('sender_id' as never, senderId)
    .is('read_at' as never, null);

  if (error) throw error;
}

export async function editMessage(messageId: string, content: string): Promise<void> {
  const { error } = await supabase
    .from('messages')
    .update({ content, edited_at: new Date().toISOString() } as never)
    .eq('id' as never, messageId);

  if (error) throw error;
}

export async function deleteMessage(messageId: string): Promise<void> {
  const { error } = await supabase
    .from('messages')
    .delete()
    .eq('id' as never, messageId);

  if (error) throw error;
}

export async function getUnreadCount(userId: string): Promise<number> {
  const { count, error } = await supabase
    .from('messages')
    .select('id', { count: 'exact', head: true })
    .eq('receiver_id' as never, userId)
    .is('read_at' as never, null);

  if (error) throw error;
  return count ?? 0;
}

// ── Group Chat functions ──

interface GroupChatRow {
  id: string;
  name: string;
  city_iata: string | null;
  range_start: string | null;
  range_end: string | null;
  created_by: string;
  created_at: string;
}

interface GroupMemberRow {
  id: string;
  group_id: string;
  user_id: string;
  deleted_at: string | null;
  joined_at: string;
}

interface GroupMessageRow {
  id: string;
  group_id: string;
  sender_id: string;
  content: string;
  metadata: GroupMessageMetadata | null;
  edited_at: string | null;
  created_at: string;
}

interface ConversationDeletionRow {
  user_id: string;
  partner_id: string;
  deleted_at: string;
}

function mapRowToGroupChat(row: GroupChatRow): GroupChat {
  return {
    id: row.id,
    name: row.name,
    cityIata: row.city_iata,
    rangeStart: row.range_start,
    rangeEnd: row.range_end,
    createdBy: row.created_by,
    createdAt: row.created_at,
  };
}

function mapRowToGroupMember(row: GroupMemberRow): GroupMember {
  return {
    id: row.id,
    groupId: row.group_id,
    userId: row.user_id,
    deletedAt: row.deleted_at,
    joinedAt: row.joined_at,
  };
}

function mapRowToGroupMessage(row: GroupMessageRow): GroupMessage {
  return {
    id: row.id,
    groupId: row.group_id,
    senderId: row.sender_id,
    content: row.content,
    metadata: row.metadata ?? null,
    editedAt: row.edited_at,
    createdAt: row.created_at,
  };
}

function mapRowToConversationDeletion(row: ConversationDeletionRow): ConversationDeletion {
  return {
    userId: row.user_id,
    partnerId: row.partner_id,
    deletedAt: row.deleted_at,
  };
}

export async function createGroupChat(
  name: string,
  memberIds: string[],
  createdBy: string,
  cityIata?: string,
  rangeStart?: string,
  rangeEnd?: string,
): Promise<{ chat: GroupChat; members: GroupMember[] }> {
  const { data: chatData, error: chatError } = await supabase
    .from('group_chats' as 'messages')
    .insert({
      name,
      city_iata: cityIata ?? null,
      range_start: rangeStart ?? null,
      range_end: rangeEnd ?? null,
      created_by: createdBy,
    } as never)
    .select()
    .single();

  if (chatError || !chatData) throw chatError ?? new Error('Failed to create group chat');
  const chat = mapRowToGroupChat(chatData as GroupChatRow);

  // Insert all members (including creator)
  // Note: no .select() — RLS SELECT policy requires the row to already exist
  // in group_members (via user_group_ids), but RETURNING runs before commit.
  const allMemberIds = [createdBy, ...memberIds.filter((id) => id !== createdBy)];
  const memberRows = allMemberIds.map((userId) => ({
    group_id: chat.id,
    user_id: userId,
  }));

  const { error: membersError } = await supabase
    .from('group_members' as 'messages')
    .insert(memberRows as never);

  if (membersError) throw membersError;

  // Construct member objects locally (avoids needing a SELECT after INSERT)
  const now = new Date().toISOString();
  const members: GroupMember[] = allMemberIds.map((userId) => ({
    id: crypto.randomUUID(),
    groupId: chat.id,
    userId,
    joinedAt: now,
    deletedAt: null,
  }));

  return { chat, members };
}

export async function getGroupChats(_userId: string): Promise<GroupChat[]> {
  const { data, error } = await supabase
    .from('group_chats' as 'messages')
    .select('*')
    .order('created_at' as never, { ascending: false });

  if (error) throw error;
  return ((data ?? []) as GroupChatRow[]).map(mapRowToGroupChat);
}

export async function getGroupMembers(_userId: string): Promise<GroupMember[]> {
  // Fetch all members for groups the user belongs to
  const { data, error } = await supabase
    .from('group_members' as 'messages')
    .select('*');

  if (error) throw error;
  return ((data ?? []) as GroupMemberRow[]).map(mapRowToGroupMember);
}

export async function getGroupMessages(_userId: string): Promise<GroupMessage[]> {
  // RLS ensures only messages from user's groups are returned
  const { data, error } = await supabase
    .from('group_messages' as 'messages')
    .select('*')
    .order('created_at' as never, { ascending: true })
    .limit(1000);

  if (error) throw error;
  return ((data ?? []) as GroupMessageRow[]).map(mapRowToGroupMessage);
}

export async function sendGroupMessage(
  groupId: string,
  senderId: string,
  content: string,
  metadata?: MeetupProposalMetadata,
): Promise<GroupMessage> {
  const row: Record<string, unknown> = { group_id: groupId, sender_id: senderId, content };
  if (metadata) row.metadata = metadata;

  const { data, error } = await supabase
    .from('group_messages' as 'messages')
    .insert(row as never)
    .select()
    .single();

  if (error || !data) throw error ?? new Error('Failed to send group message');
  return mapRowToGroupMessage(data as GroupMessageRow);
}

export async function voteOnProposal(
  messageId: string,
  userId: string,
  vote: 'yes' | 'no',
): Promise<MeetupProposalMetadata | null> {
  // Read current metadata
  const { data: row, error: readErr } = await supabase
    .from('group_messages' as 'messages')
    .select('metadata')
    .eq('id' as never, messageId)
    .single();

  if (readErr || !row) throw readErr ?? new Error('Message not found');
  const current = (row as { metadata: MeetupProposalMetadata | null }).metadata;
  if (!current || current.type !== 'meetup_proposal') return null;

  const updated: MeetupProposalMetadata = {
    ...current,
    votes: { ...current.votes, [userId]: vote },
  };

  const { error: writeErr } = await supabase
    .from('group_messages' as 'messages')
    .update({ metadata: updated } as never)
    .eq('id' as never, messageId);

  if (writeErr) throw writeErr;
  return updated;
}

export async function editGroupMessage(messageId: string, content: string): Promise<void> {
  const { error } = await supabase
    .from('group_messages' as 'messages')
    .update({ content, edited_at: new Date().toISOString() } as never)
    .eq('id' as never, messageId);

  if (error) throw error;
}

export async function deleteGroupMessage(messageId: string): Promise<void> {
  const { error } = await supabase
    .from('group_messages' as 'messages')
    .delete()
    .eq('id' as never, messageId);

  if (error) throw error;
}

export async function renameGroupChat(groupId: string, name: string): Promise<void> {
  const { error } = await supabase
    .from('group_chats' as 'messages')
    .update({ name } as never)
    .eq('id' as never, groupId);

  if (error) throw error;
}

export async function softDeleteGroupChat(groupId: string, userId: string): Promise<void> {
  const { error } = await supabase.rpc('leave_group' as never, {
    p_group_id: groupId,
    p_user_id: userId,
  } as never);

  if (error) throw error;
}

export async function rejoinGroupChat(groupId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('group_members' as 'messages')
    .update({ deleted_at: null, joined_at: new Date().toISOString() } as never)
    .eq('group_id' as never, groupId)
    .eq('user_id' as never, userId);

  if (error) throw error;
}

export async function softDeleteDmConversation(userId: string, partnerId: string): Promise<void> {
  const { error } = await supabase
    .from('conversation_deletions' as 'messages')
    .upsert(
      { user_id: userId, partner_id: partnerId, deleted_at: new Date().toISOString() } as never,
      { onConflict: 'user_id,partner_id' },
    );

  if (error) throw error;
}

export async function getConversationDeletions(userId: string): Promise<ConversationDeletion[]> {
  const { data, error } = await supabase
    .from('conversation_deletions' as 'messages')
    .select('*')
    .eq('user_id' as never, userId);

  if (error) throw error;
  return ((data ?? []) as ConversationDeletionRow[]).map(mapRowToConversationDeletion);
}

// ── Meetup functions ──

interface MeetupRow {
  id: string;
  group_chat_id: string;
  created_by: string;
  city: string;
  date: string;
  time: string | null;
  place: string | null;
  activity: string | null;
  status: 'proposed' | 'confirmed' | 'cancelled';
  message_id: string | null;
  created_at: string;
}

interface MeetupRsvpRow {
  id: string;
  meetup_id: string;
  user_id: string;
  status: 'pending' | 'yes' | 'no';
  auto_declined: boolean;
  responded_at: string | null;
}

function mapRowToMeetup(row: MeetupRow): Meetup {
  return {
    id: row.id,
    groupChatId: row.group_chat_id,
    createdBy: row.created_by,
    city: row.city,
    date: row.date,
    time: row.time,
    place: row.place,
    activity: row.activity,
    status: row.status,
    messageId: row.message_id,
    createdAt: row.created_at,
  };
}

function mapRowToMeetupRsvp(row: MeetupRsvpRow): MeetupRsvp {
  return {
    id: row.id,
    meetupId: row.meetup_id,
    userId: row.user_id,
    status: row.status,
    autoDeclined: row.auto_declined,
    respondedAt: row.responded_at,
  };
}

export async function getMeetups(): Promise<Meetup[]> {
  const { data, error } = await supabase
    .from('meetups' as 'messages')
    .select('*')
    .order('date' as never, { ascending: true });

  if (error) throw error;
  return ((data ?? []) as MeetupRow[]).map(mapRowToMeetup);
}

export async function getMeetupRsvps(): Promise<MeetupRsvp[]> {
  const { data, error } = await supabase
    .from('meetup_rsvps' as 'messages')
    .select('*');

  if (error) throw error;
  return ((data ?? []) as MeetupRsvpRow[]).map(mapRowToMeetupRsvp);
}

export async function createMeetup(params: {
  groupChatId: string;
  createdBy: string;
  city: string;
  date: string;
  time?: string;
  place?: string;
  activity?: string;
  messageId?: string;
  memberIds: string[];
}): Promise<Meetup> {
  const { data, error } = await supabase
    .from('meetups' as 'messages')
    .insert({
      group_chat_id: params.groupChatId,
      created_by: params.createdBy,
      city: params.city,
      date: params.date,
      time: params.time ?? null,
      place: params.place ?? null,
      activity: params.activity ?? null,
      message_id: params.messageId ?? null,
    } as never)
    .select()
    .single();

  if (error || !data) throw error ?? new Error('Failed to create meetup');
  // RSVPs are auto-seeded by the seed_meetup_rsvps DB trigger (SECURITY DEFINER)
  return mapRowToMeetup(data as MeetupRow);
}

export async function rsvpMeetup(
  meetupId: string,
  userId: string,
  status: 'yes' | 'no',
): Promise<void> {
  const { error } = await supabase
    .from('meetup_rsvps' as 'messages')
    .upsert({
      meetup_id: meetupId,
      user_id: userId,
      status,
      auto_declined: false,
      responded_at: new Date().toISOString(),
    } as never, { onConflict: 'meetup_id,user_id' });

  if (error) throw error;
}

export async function cancelMeetup(meetupId: string): Promise<void> {
  const { error } = await supabase
    .from('meetups' as 'messages')
    .update({ status: 'cancelled' } as never)
    .eq('id' as never, meetupId);

  if (error) throw error;
}

// ── Data Export (GDPR Art. 20 portability) ──

export async function exportUserData(userId: string): Promise<Record<string, unknown>> {
  const [profile, flights, friendships, messages, groupChats, groupMessages, meetups] =
    await Promise.all([
      supabase.from('profiles').select('*').eq('id' as never, userId).single(),
      supabase.from('flights').select('*').eq('user_id' as never, userId).order('date' as never, { ascending: false }),
      supabase.from('friendships').select('*').or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`),
      supabase.from('messages').select('*').or(`sender_id.eq.${userId},receiver_id.eq.${userId}`).order('created_at' as never, { ascending: true }),
      supabase.from('group_chats' as 'messages').select('*'),
      supabase.from('group_messages' as 'messages').select('*').eq('sender_id' as never, userId),
      supabase.from('meetups' as 'messages').select('*'),
    ]);

  const profileData = profile.data as Record<string, unknown> | null;
  if (profileData) {
    delete profileData.apns_token;
    delete profileData.phone_hash;
  }

  return {
    exported_at: new Date().toISOString(),
    profile: profileData,
    flights: flights.data ?? [],
    friendships: friendships.data ?? [],
    messages: messages.data ?? [],
    group_chats: groupChats.data ?? [],
    group_messages_sent: groupMessages.data ?? [],
    meetups: meetups.data ?? [],
  };
}
